import { TmapApp } from '@tmap-web-lib/tmap-app-interface'
import { useAtom, useSetAtom } from 'jotai'
import { PropsWithChildren, useEffect, useMemo } from 'react'
import { gte } from 'semver'

import { usePageIdContext } from '@/Providers'
import { globalState, storageSchemeUrl } from '@/atoms'

function parseExtra(extra: unknown): Record<string, any> {
  if (typeof extra !== 'string' || !extra) return {}

  try {
    return JSON.parse(extra)
  } catch {
    try {
      return JSON.parse(decodeURIComponent(extra))
    } catch {
      return {}
    }
  }
}

function buildNormalizedPath(params: {
  pageId?: string
  rawExtra?: unknown
  appVersion: string
}) {
  const { pageId, rawExtra, appVersion } = params
  const extraObject = parseExtra(rawExtra)

  switch (pageId) {
  case 'open_profile': {
    const nextExtra = {
      ...extraObject,
      toggleTab: 'openProfile',
    }

    const encodedExtra = encodeURIComponent(JSON.stringify(nextExtra))

    return gte(appVersion, '11.0.0')
      ? `/web/my?extra=${encodedExtra}`
      : '/web'
  }

  default:
    return null
  }
}

export function CheckPageId({ children }: PropsWithChildren) {
  const pageIdContext = usePageIdContext()
  const setGlobalState = useSetAtom(globalState)
  const [schemeUrlStorageValue, setSchemeUrlStorageValue] = useAtom(storageSchemeUrl)

  const normalizedPath = useMemo(() => {
    const pageId =
      pageIdContext?.pageIdInfo?.pageid ||
      pageIdContext?.searchParams?.pageid

    const rawExtra = pageIdContext?.searchParams?.extra

    if (TmapApp.env.appVersion === '0.0.0') return null

    return buildNormalizedPath({
      pageId,
      rawExtra,
      appVersion: TmapApp.env.appVersion,
    })
  }, [pageIdContext])

  useEffect(() => {
    if (!normalizedPath) return

    const currentPath = `${window.location.pathname}${window.location.search}`
    if (currentPath === normalizedPath) return

    if (TmapApp.env.isAndroid) {
      setGlobalState((prev) => ({ ...prev, schemeURL: normalizedPath }))
      setSchemeUrlStorageValue(normalizedPath)
      window.history.back()
      return
    }

    window.location.replace(normalizedPath)
  }, [normalizedPath, setGlobalState, setSchemeUrlStorageValue])

  useEffect(() => {
    if (!schemeUrlStorageValue) return

    window.location.replace(schemeUrlStorageValue)

    setTimeout(() => {
      setGlobalState((prev) => ({ ...prev, schemeURL: '' }))
    }, 0)

    setSchemeUrlStorageValue('')
  }, [schemeUrlStorageValue, setGlobalState, setSchemeUrlStorageValue])

  return <>{children}</>
}